<script setup lang="ts">
/**
 * 画像OCR NGワードチェック: 画像の上にNGワードを含む行の枠を重ねて描く。
 *
 * 【枠の描き方】
 * 画像と同じ大きさのSVGを重ね、viewBox を OCR時の画像サイズ(ocr.size)にする。
 * 枠の座標(OCR時のピクセル座標)をそのまま rect に渡せば、画像がどの大きさで
 * 表示されていても(拡大縮小・ウィンドウサイズ変更)枠がずれない。
 * ラッパーは画像と同じ大きさになる(img に縦横比を保った max-width/max-height を
 * 付けているため)ので、preserveAspectRatio="none" で画像の表示領域にぴったり合わせる。
 * ocr.size が無い古いデータは、読み込んだ画像の実寸(naturalWidth/Height)で代用する。
 *
 * 【強調表示】
 * activeIndex に hits の添字を渡すと、その語の枠だけ太く・塗りありにし、他の枠は薄くする。
 * showAll=false(枠を非表示)のときは、activeIndex の語の枠だけを描く
 * (一覧の行にマウスを乗せている間だけ、その語の場所が分かるようにするため)。
 */
import { computed, ref, watch } from 'vue'
import type { ProductImageOcr } from '@/types/api'
import { ocrHits, levelClass } from '@/utils/ocrNg'

const props = withDefaults(
  defineProps<{
    src: string
    ocr?: ProductImageOcr
    /** 強調する hits の添字。null なら全枠を通常表示 */
    activeIndex?: number | null
    /** false なら全枠を非表示にし、activeIndex の語の枠だけを描く */
    showAll?: boolean
    imgClass?: string
    alt?: string
  }>(),
  { activeIndex: null, showAll: true, imgClass: '', alt: '' },
)

const natural = ref<[number, number] | null>(null)
watch(
  () => props.src,
  () => (natural.value = null),
)
function onLoad(e: Event) {
  const img = e.target as HTMLImageElement
  natural.value = [img.naturalWidth, img.naturalHeight]
}

const hits = computed(() => ocrHits(props.ocr))
const size = computed(() => props.ocr?.size ?? natural.value)

/** 文字にぴったりの枠だと文字と重なって読みにくいため、画像の長辺の0.6%だけ外側に広げる */
const pad = computed(() => (size.value ? Math.max(size.value[0], size.value[1]) * 0.006 : 0))

/**
 * 描画順はレベルの低い語から(重なったときに高レベルの枠が上に来るように)。
 * 強調中の語は最後に描いて最前面に出す。
 */
const drawOrder = computed(() =>
  hits.value
    .map((h, i) => ({ h, i }))
    .filter(({ i }) => props.showAll || i === props.activeIndex)
    .sort((a, b) => {
      if (a.i === props.activeIndex) return 1
      if (b.i === props.activeIndex) return -1
      return a.h.level - b.h.level
    }),
)
</script>

<template>
  <div class="ocr-img-wrap">
    <img :src="src" :class="imgClass" :alt="alt" @load="onLoad" />
    <svg
      v-if="size && drawOrder.length > 0"
      class="ocr-overlay"
      :viewBox="`0 0 ${size[0]} ${size[1]}`"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <g
        v-for="{ h, i } in drawOrder"
        :key="i"
        class="ocr-hit"
        :class="[levelClass(h.level), { active: activeIndex === i, dim: showAll && activeIndex !== null && activeIndex !== i }]"
      >
        <rect
          v-for="(b, bi) in h.boxes"
          :key="bi"
          :x="b[0] - pad"
          :y="b[1] - pad"
          :width="b[2] - b[0] + pad * 2"
          :height="b[3] - b[1] + pad * 2"
          vector-effect="non-scaling-stroke"
        />
      </g>
    </svg>
  </div>
</template>
